"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AUTH_SESSION_EXPIRED_EVENT, ensureValidAccessToken } from "./http";
import { authApi, extractUserId, flattenEnterpriseRoles, type LoginEnterprise } from "./api";
import { storage, type FareAuthUser } from "./storage";
import { saveLoginSession } from "./session";
import { FARE_RETAIL_ID, getCurrentEnterpriseId, setCurrentEnterpriseId } from "@/lib/lms/enterprise-api";

type AuthContextType = {
  user: FareAuthUser | null;
  loading: boolean;
  /**
   * Raw /auth/me payload from the most recent fetch — held in memory only,
   * never restored from storage, so permissions derived from it
   * (lib/permissions) always reflect the server. Null until fetched or when
   * the last fetch failed (see meError).
   */
  me: Record<string, unknown> | null;
  meError: string;
  /** Re-fetches /auth/me — called on enterprise switch and after a 403. */
  refreshMe: () => Promise<void>;
  login: (payload: { username: string; password: string }) => Promise<void>;
  logout: () => void;
  /** Merges partial fields into the signed-in user, in both context state and storage — used by the Profile screen after a successful details save. */
  updateUser: (partial: Partial<FareAuthUser>) => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

function navigateWithinCurrentOrigin(path: string, mode: "assign" | "replace") {
  const target = new URL(path, window.location.origin).toString();
  if (mode === "replace") {
    window.location.replace(target);
  } else {
    window.location.assign(target);
  }
}

function toAuthUser(
  username: string,
  token: string,
  auth: Record<string, unknown>,
  me?: Record<string, unknown>,
  previousSelectedEnterpriseId?: string,
): FareAuthUser {
  const userId = extractUserId(me, token) || extractUserId(auth, token);
  const userName =
    (typeof me?.name === "string" && me.name) ||
    (typeof me?.username === "string" && me.username) ||
    username;
  const userEmail = typeof me?.email === "string" ? me.email : "";

  // The login response's `enterprises` field — a user can hold a different
  // role per Enterprise (and per Department/Team within one), flattened
  // into a single list. Preserves any previously-selected default across a
  // session restore where a fresh login response isn't available.
  const enterpriseRoles = flattenEnterpriseRoles(auth.enterprises as LoginEnterprise[] | undefined);
  const selectedEnterpriseId =
    (previousSelectedEnterpriseId &&
    (previousSelectedEnterpriseId === FARE_RETAIL_ID ||
      enterpriseRoles.some((r) => r.enterpriseId === previousSelectedEnterpriseId))
      ? previousSelectedEnterpriseId
      : enterpriseRoles[0]?.enterpriseId) || previousSelectedEnterpriseId;

  return {
    userId,
    username,
    userName,
    userEmail,
    userData: me,
    enterpriseRoles: enterpriseRoles.length > 0 ? enterpriseRoles : undefined,
    selectedEnterpriseId,
  };
}

// Refreshes the user from a fresh /auth/me. /auth/me carries the same
// `enterprises` tree as the login response, so the enterprise/role list is
// always rebuilt from the server, and the selected enterprise is re-validated
// against it (falls back to the first enterprise if it's no longer valid).
function mergeMe(stored: FareAuthUser, token: string, me: Record<string, unknown>): FareAuthUser {
  const hasEnterprises = Array.isArray(me.enterprises);
  const preferred = getCurrentEnterpriseId() || stored.selectedEnterpriseId;
  const fresh = toAuthUser(stored.username, token, hasEnterprises ? me : {}, me, preferred);
  return {
    ...stored,
    userId: fresh.userId || stored.userId,
    userName: fresh.userName || stored.userName,
    userEmail: fresh.userEmail || stored.userEmail,
    userData: me,
    ...(hasEnterprises
      ? { enterpriseRoles: fresh.enterpriseRoles, selectedEnterpriseId: fresh.selectedEnterpriseId }
      : {}),
  };
}

// Keeps lib/lms/enterprise-api's "current enterprise" (read by Courses,
// Teams, etc.) identical to the one permissions are evaluated for.
function syncCurrentEnterprise(user: FareAuthUser) {
  if (user.selectedEnterpriseId && user.selectedEnterpriseId !== getCurrentEnterpriseId()) {
    setCurrentEnterpriseId(user.selectedEnterpriseId);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<FareAuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<Record<string, unknown> | null>(null);
  const [meError, setMeError] = useState("");

  // /auth/me is fetched on every page load (here) and every login (below) —
  // never served from storage.
  useEffect(() => {
    let active = true;
    const stored = storage.getUser<FareAuthUser>();

    async function restoreSession() {
      try {
        const token = await ensureValidAccessToken();
        if (!token) {
          if (active) {
            storage.clear();
            setUser(null);
          }
          return;
        }
        const res = await authApi.getMe();
        if (!active) return;
        if (res.success && res.data) {
          const next = mergeMe(
            stored ?? { userId: "", username: "", userName: "", userEmail: "" },
            token,
            res.data,
          );
          storage.setUser(next);
          syncCurrentEnterprise(next);
          setUser(next);
          setMe(res.data);
          setMeError("");
        } else if (stored) {
          // Keep the session but grant nothing until /auth/me succeeds.
          setUser(stored);
          setMe(null);
          setMeError(res.error || "Could not load your permissions.");
        } else {
          storage.clear();
          setUser(null);
        }
      } catch {
        if (active) setUser(null);
      } finally {
        if (active) setLoading(false);
      }
    }

    void restoreSession();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    function onSessionExpired() {
      setUser(null);
      setMe(null);
      const path = window.location.pathname;
      const publicPaths = [
        "/",
        "/login",
        "/signup",
        "/otp-verification",
        "/create-password",
        "/login-otp",
        "/login-otp/verify",
        "/login-otp/set-password",
      ];
      if (publicPaths.includes(path)) return;
      navigateWithinCurrentOrigin("/login", "replace");
    }

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  const login = useCallback(async (payload: { username: string; password: string }) => {
    storage.clear();
    const response = await authApi.login(payload);
    if (!response.success || !response.data?.token) {
      throw new Error(response.error || "Login response did not include an access token.");
    }

    const auth = response.data;
    storage.setTokens(auth.token, auth.refreshToken);

    let me: Record<string, unknown> | undefined;
    let meFailure = "";
    try {
      const meResponse = await authApi.getMe();
      if (meResponse.success && meResponse.data) me = meResponse.data;
      else meFailure = meResponse.error || "Could not load your permissions.";
    } catch (error) {
      if (!storage.getToken()) throw error;
      me = undefined;
      meFailure = "Could not load your permissions.";
    }
    setMe(me ?? null);
    setMeError(meFailure);

    const nextUser = toAuthUser(payload.username, auth.token, auth, me, getCurrentEnterpriseId());
    saveLoginSession({
      token: auth.token,
      refreshToken: auth.refreshToken,
      userId: nextUser.userId,
      username: nextUser.username,
      userName: nextUser.userName,
      userEmail: nextUser.userEmail,
      userData: me,
      enterpriseRoles: nextUser.enterpriseRoles,
      selectedEnterpriseId: nextUser.selectedEnterpriseId,
    });
    setUser(nextUser);

    // Permissions are evaluated for this enterprise, so the app-wide
    // "current enterprise" must match it.
    syncCurrentEnterprise(nextUser);
   //  navigateWithinCurrentOrigin("/dashboard", "assign");
  }, []);

  const refreshMe = useCallback(async () => {
    const res = await authApi.getMe();
    if (res.success && res.data) {
      const data = res.data;
      setMe(data);
      setMeError("");
      setUser((prev) => {
        if (!prev) return prev;
        const next = mergeMe(prev, storage.getToken(), data);
        storage.setUser(next);
        syncCurrentEnterprise(next);
        return next;
      });
    } else {
      setMe(null);
      setMeError(res.error || "Could not load your permissions.");
    }
  }, []);

  const logout = useCallback(() => {
    storage.clear();
    setUser(null);
    setMe(null);
    setMeError("");
    navigateWithinCurrentOrigin("/login", "replace");
  }, []);

  const updateUser = useCallback((partial: Partial<FareAuthUser>) => {
    setUser((prev) => {
      const base: FareAuthUser = prev ?? {
        userId: "",
        username: "",
        userName: "",
        userEmail: "",
        userData: undefined,
      };
      const next: FareAuthUser = {
        ...base,
        ...partial,
        userData: { ...(base.userData || {}), ...(partial.userData || {}) },
      };
      storage.setUser(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      me,
      meError,
      refreshMe,
      login,
      logout,
      updateUser,
    }),
    [user, loading, me, meError, refreshMe, login, logout, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
